import { useEffect, useRef, useState } from 'react';
import type { BarcodeFormat } from 'barcode-detector/pure';
import type { ZXingModuleOverrides } from 'zxing-wasm/reader';
import zxingReaderWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';

export interface ScanResult {
  ok: boolean;
  message: string;
}

interface BarcodeScannerDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * 'continuous' (default) — keeps the camera open after each scan, e.g. for
   * adding a whole batch of serial numbers. onDetected reports back whether
   * the code was added so the dialog can show inline feedback.
   * 'single' — closes the dialog right after the first scan, e.g. for
   * filling one product code field.
   */
  mode?: 'continuous' | 'single';
  onDetected: (value: string) => Promise<ScanResult> | void;
}

const BARCODE_FORMATS: BarcodeFormat[] = [
  'code_128',
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'qr_code',
  'data_matrix',
];

// After a code is handled, pause detection for this long so the same physical
// label isn't re-fired every frame while it's still in view.
const RESULT_COOLDOWN_MS = 1200;

// Minimum gap between detect() calls. No need to run a full WASM decode at
// the display's refresh rate — this caps CPU/battery use on mobile.
const DETECT_INTERVAL_MS = 150;

// If detect() keeps throwing this many times in a row, something is actually
// broken (not just a frame-not-ready blip) — stop and surface it.
const MAX_CONSECUTIVE_ERRORS = 50;

// Serve the reader .wasm from our own build instead of the library's default
// jsDelivr CDN fallback, so scanning keeps working behind restrictive network policies.
const WASM_OVERRIDES: ZXingModuleOverrides = {
  locateFile: (path: string, prefix: string) =>
    path.endsWith('.wasm') ? zxingReaderWasmUrl : prefix + path,
};

export default function BarcodeScannerDialog({
  open,
  onClose,
  mode = 'continuous',
  onDetected,
}: BarcodeScannerDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectTimeoutRef = useRef<number | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const onDetectedRef = useRef(onDetected);
  const onCloseRef = useRef(onClose);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ScanResult | null>(null);
  const [addedCount, setAddedCount] = useState(0);

  useEffect(() => {
    onDetectedRef.current = onDetected;
    onCloseRef.current = onClose;
  }, [onDetected, onClose]);

  const stopScanning = () => {
    if (detectTimeoutRef.current !== null) {
      clearTimeout(detectTimeoutRef.current);
      detectTimeoutRef.current = null;
    }
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (!open) {
      stopScanning();
      return;
    }

    let cancelled = false;
    setStarting(true);
    setError(null);
    setLastResult(null);
    setAddedCount(0);
    consecutiveErrorsRef.current = 0;

    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const startScanning = async () => {
      try {
        const [{ BarcodeDetector, prepareZXingModule }, stream] = await Promise.all([
          import('barcode-detector/pure'),
          navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } }),
        ]);
        prepareZXingModule({ overrides: WASM_OVERRIDES });

        if (cancelled) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const detector = new BarcodeDetector({ formats: BARCODE_FORMATS });
        setStarting(false);

        const tick = async () => {
          if (cancelled || !videoRef.current) {
            return;
          }

          try {
            const codes = await detector.detect(videoRef.current);
            if (cancelled) {
              return;
            }
            consecutiveErrorsRef.current = 0;

            if (codes.length > 0) {
              const result = await onDetectedRef.current(codes[0].rawValue);
              if (cancelled) {
                return;
              }

              if (mode === 'single' && (!result || result.ok)) {
                stopScanning();
                onCloseRef.current();
                return;
              }

              if (result) {
                setLastResult(result);
                if (mode === 'continuous' && result.ok) {
                  setAddedCount(count => count + 1);
                }
              }

              await sleep(RESULT_COOLDOWN_MS);
              if (cancelled) {
                return;
              }
              setLastResult(null);
            }
          } catch (err) {
            // transient decode errors (e.g. frame not ready yet) are expected —
            // but if they never stop, something is actually broken.
            consecutiveErrorsRef.current += 1;
            console.error('Barcode detection error:', err);
            if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
              stopScanning();
              if (!cancelled) {
                setError('Помилка сканування. Спробуйте ще раз.');
              }
              return;
            }
          }

          if (!cancelled) {
            detectTimeoutRef.current = window.setTimeout(tick, DETECT_INTERVAL_MS);
          }
        };

        detectTimeoutRef.current = window.setTimeout(tick, DETECT_INTERVAL_MS);
      } catch (err) {
        console.error('Error starting barcode scanner:', err);
        if (!cancelled) {
          setStarting(false);
          setError(
            'Не вдалося отримати доступ до камери. Перевірте дозволи браузера на камеру.'
          );
        }
      }
    };

    startScanning();

    return () => {
      cancelled = true;
      stopScanning();
    };
  }, [open, mode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        Сканування коду
        {mode === 'continuous' && addedCount > 0 && (
          <Chip label={`Додано: ${addedCount}`} color="success" size="small" />
        )}
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4 / 3',
              bgcolor: 'black',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {starting && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress sx={{ color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Запуск камери…
                </Typography>
              </Box>
            )}
            {lastResult && (
              <Alert
                severity={lastResult.ok ? 'success' : 'warning'}
                sx={{
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: 8,
                }}
              >
                {lastResult.message}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {mode === 'single' ? 'Скасувати' : 'Готово'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
